import React, {
  useEffect, useRef, useState,
} from 'react';
import style from './pdfPreviewModal.module.css';
import mainStyles from '../../pages/main.module.css';
import Button from '../Button/button';
import { useCreateDownloadFileLogApi } from '../../hooks/api/log.hook';
import { OcrPreviewModal } from '../OcrPreviewModal';
import Validator from '../../utils/validators';
import AlertModal, { alertModalInitialState, AlertModalProps } from '../AlertModal';
import PdfPreviewError from '../PdfPreview/PdfPreviewError';
import FileUtils, { PDF_PASSWORD_PROTECTED_ERROR } from '../../utils/file.utils';
import { usePdfViewerControl } from '../../hooks/usePdfViewerControl';

export interface PdfPreviewProps {
  open: boolean;
  onClose: () => void;
  pdfFile: File | null;
  fileName: string;
  fileId: number;
  showOcrSearchPreview?: boolean,
  documentVersionNo?: number,
  documentId?: number,
  highlightTexts?: string[],
  searchKeyword?: string,
}

export function PdfPreviewModal({
  open = false,
  onClose,
  pdfFile,
  fileName,
  fileId,
  showOcrSearchPreview = false,
  documentVersionNo,
  documentId,
  highlightTexts = [],
  searchKeyword,
}: PdfPreviewProps) {
  const { request: createDownloadLog } = useCreateDownloadFileLogApi();

  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [openOcrPreviewModal, setOpenOcrPreviewModal] = useState(false);
  const [modal, setModal] = useState<AlertModalProps>(alertModalInitialState);
  const [errorMessage, setErrorMessage] = useState('');

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { passwordCancelled, resetPasswordCancelled } = usePdfViewerControl(iframeRef);

  const downloadFile = (file: File | null, pdfFileName: string) => {
    if (!file) return;
    createDownloadLog(fileId, 0);

    const url = URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = pdfFileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCloseModal = () => {
    setDataUrl(null);
    resetPasswordCancelled();
    onClose();
  };

  useEffect(() => {
    resetPasswordCancelled();

    if (pdfFile) {
      let cancelled = false;
      const url = URL.createObjectURL(pdfFile);
      Validator.validateDocumentFile(pdfFile).then((error) => {
        if (cancelled) {
          return;
        }
        const pdfFileError = FileUtils.getPdfDisplayErrorMessage(error);
        setDataUrl(pdfFileError ? null : url);
        setErrorMessage(pdfFileError);
      });

      return () => {
        cancelled = true;
        URL.revokeObjectURL(url);
      };
    }
    setDataUrl(null);
    setErrorMessage('');
    return undefined;
  }, [pdfFile, resetPasswordCancelled]);

  useEffect(() => {
    localStorage.removeItem('pdfjs.history');

    return () => {
      localStorage.removeItem('pdfjs.history');
    };
  }, []);

  const displayErrorMessage = passwordCancelled
    ? FileUtils.getPdfDisplayErrorMessage(PDF_PASSWORD_PROTECTED_ERROR)
    : errorMessage;

  return (
    <>
      <div className={style.modalOverlay} style={{ display: open ? 'block' : 'none' }}>
        <div className={style.alertBox}>
          <div className={style.modal}>
            <div className={style.modalContainer}>
              <div className={style.modalBody}>
                {dataUrl && !passwordCancelled && (
                  <iframe ref={iframeRef} src={`pdfjs/web/viewer.html?file=${dataUrl}`} style={{ width: '100%', height: '100%' }} title="pdf" />
                )}
                {displayErrorMessage && <PdfPreviewError errorMessage={displayErrorMessage} />}
              </div>
              <div className={style.modalFooter}>
                <Button className={mainStyles['mr-10']} color="lightGray" size="smaller" onClick={handleCloseModal}>閉じる</Button>
                {showOcrSearchPreview && (
                <Button
                  className={mainStyles['mr-10']}
                  color="lightGray"
                  size="smaller"
                  onClick={() => {
                    setOpenOcrPreviewModal(true);
                  }}
                >
                  OCR結果
                </Button>
                )}
                <Button
                  size="smaller"
                  onClick={() => {
                    downloadFile(pdfFile, fileName);
                  }}
                >
                  ダウンロード
                </Button>
              </div>
            </div>
          </div>
        </div>
        {openOcrPreviewModal && (
        <OcrPreviewModal
          open
          onClose={() => {
            setOpenOcrPreviewModal(false);
          }}
          pdfFile={pdfFile}
          documentId={documentId}
          versionNo={documentVersionNo}
          highlightTexts={highlightTexts}
          searchKeyword={searchKeyword}
        />
        )}
      </div>
      <AlertModal
        modalStyle={modal.modalStyle}
        open={modal.open}
        text={modal.text}
        confirmText={modal.confirmText}
        onCancel={modal.onCancel}
        onConfirm={modal.onConfirm}
        noCancel={modal.noCancel}
      />
    </>
  );
}
